import anime from "animejs";
import cx from "classnames";
import { useStaticQuery, graphql } from "gatsby";
import Img from "gatsby-image";
import * as React from "react";
import ReactPixel from "react-facebook-pixel";
import { useForm } from "react-hook-form";
import LinkedInTag from "react-linkedin-insight";
import * as yup from "yup";

import styles from "./searchResult.module.scss";
import { FormDataProps, sendDataToApi } from "../../utils/client";
import { StoreContext } from "../../utils/storeContext";
import { gtagService } from "../../utils/gtag";

const linkToPdf = process.env.GATSBY_PDF_LINK;

interface Salary {
  family: string;
  role: string;
  experienceYears: string;
  manager: boolean;
  localisation: string;
  fixMin: number;
  fixMax: number;
  varMin: number;
  varMax: number;
}

yup.setLocale({
  mixed: {
    required: ({ path }: any) => `Le champ ${fieldTrans[path]} est obligatoire.`,
  },
  string: {
    email: ({ value }: any) => `L'email "${value}" n'est pas un email valide.`,
  },
});

const VallidationSchema = yup.object().shape({
  email: yup
    .string()
    .email()
    .required(),
});

const fieldTrans: { [key: string]: string } = {
  email: "Email",
};

const SearchResult: React.FC = () => {
  const data = useStaticQuery(graphql`
    query {
      logoEMail: file(relativePath: { eq: "logo-email.png" }) {
        childImageSharp {
          fluid(maxWidth: 500) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      logoFacebook: file(relativePath: { eq: "logo-facebook-white.png" }) {
        childImageSharp {
          fluid(maxWidth: 500) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      logoLinkedin: file(relativePath: { eq: "logo-linkedin-white.png" }) {
        childImageSharp {
          fluid(maxWidth: 500) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const { state } = React.useContext(StoreContext);

  const [mobile] = React.useState<boolean>(typeof window !== `undefined` ? window.innerWidth <= 750 : false);
  const [dirtyIds, setDirtyIds] = React.useState<string[]>([]);
  const [invalidIds, setInvalidIds] = React.useState<string[]>([]);
  const [hasValueIds, setHasValueIds] = React.useState<string[]>([]);
  const [focusedInput, setFocusedInput] = React.useState<string>("");
  const [apiResponse, setApiResponse] = React.useState<string>("");
  const [disableBtn, setDisableBtn] = React.useState<boolean>(false);
  const [showDownloadCta, setShowDownloadCta] = React.useState<boolean>(false);
  const [animateLoader, setAnimateLoader] = React.useState<boolean>(false);
  const [successLoader, setSuccessLoader] = React.useState<boolean>(false);
  const [failLoader, setFailLoader] = React.useState<boolean>(false);
  const [openShareModal, setOpenShareModal] = React.useState<boolean>(false);
  const btnSubmitContainer = React.useRef(null);
  const checkCrossContainer = React.useRef(null);
  const btnDownload = React.useRef(null);
  const loader = React.useRef(null);
  const pdfCta = React.useCallback(node => {
    if (node !== null) {
      triggerPdfDownload(node);
    }
  }, []);

  const shareLink: string = "simulateur-btob.elinoi.com/";

  const linkedinLink = ` http://www.linkedin.com/shareArticle?mini=true&url=https://${encodeURI(shareLink)}`;
  const [salaries, setSalaries] = React.useState<Salary[]>([]);

  const { handleSubmit, register, triggerValidation, errors } = useForm<FormDataProps>({
    validationSchema: VallidationSchema,
  });

  const sendValues = (val: FormDataProps) => {
    sendDataToApi({
      ...val,
      type: "entreprise",
      operation: "simulateur-btob",
    })
      .then(() => {
        triggerConversion();
        gtagService.event("click", {
          event_label: "pdf - formulaire - telecharger",
        });
        setApiResponse("success");
      })
      .catch(() => setApiResponse("fail"));
  };
  const onSubmitForm = (values: FormDataProps) => {
    setDisableBtn(true);
    setApiResponse("");
    showSubmitingState(() => sendValues(values));
  };

  const showSubmitingState = (callback: () => void) => {
    setSuccessLoader(false);
    setFailLoader(false);
    const tl = anime.timeline({
      complete: () => {
        setAnimateLoader(true);
        if (typeof callback === "function") {
          callback();
        }
      },
      duration: 200,
      easing: "cubicBezier(1,0,.27,.97)",
    });

    tl.add({
      easing: "linear",
      opacity: 0,
      targets: btnDownload.current,
    })
      .add({
        duration: 300,
        targets: btnSubmitContainer.current,
        width: 34,
      })
      .add({
        duration: 1,
        opacity: 1,
        targets: loader.current,
      })
      .add({
        boxShadow: "inset 0 0 0 4px #6bcabf",
        duration: 1,
        targets: btnSubmitContainer.current,
      });
  };

  const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(shareLink)}`;

  const gmailLink = `https://mail.google.com/mail/?extsrc=mailto&url=mailto:?body=https://${encodeURI(shareLink)}`;

  const filterSalaries = (localisation: string) => {
    const arr: JSX.Element[] = [];
    salaries &&
      salaries.forEach((salary: Salary) => {
        if (salary.role === state.role && salary.localisation === localisation && salary.manager === state.management) {
          state.range.includes(salary.experienceYears)
            ? arr.push(
                <td key={salary["@id"]} className={styles.resultSalaries}>
                  <div>
                    <p className={styles.salaries}>
                      {salary.fixMin} à {salary.fixMax}k
                    </p>
                    <p className={styles.package}>
                      {salary.varMin} à {salary.varMax}k
                    </p>
                  </div>
                </td>,
              )
            : arr.push(
                <td key={salary["@id"]} className={styles.resultSalariesHidden}>
                  <div>
                    <p className={styles.salaries}>
                      {salary.fixMin} à {salary.fixMax}k
                    </p>
                    <p className={styles.package}>
                      {salary.varMin} à {salary.varMax}k
                    </p>
                  </div>
                </td>,
              );
        }
      });
    return arr;
  };

  const filterSalariesForMobile = (localisation: string, range: string) => {
    let partOfArray = <td>27/27</td>;
    salaries &&
      salaries.forEach((salary: Salary) => {
        if (salary.role === state.role && salary.localisation === localisation && salary.manager === state.management) {
          if (state.range.includes(range) && range === salary.experienceYears) {
            partOfArray = (
              <td className={styles.resultSalaries}>
                <div>
                  <p className={styles.salaries}>
                    {salary.fixMin} à {salary.fixMax}k
                  </p>
                  <p className={styles.package}>
                    {salary.varMin} à {salary.varMax}k
                  </p>
                </div>
              </td>
            );
          }
          if (!state.range.includes(range) && range === salary.experienceYears) {
            partOfArray = (
              <td key={salary["@id"]} className={styles.resultSalariesHidden}>
                <div>
                  <p className={styles.salaries}>
                    {salary.fixMin} à {salary.fixMax}k
                  </p>
                  <p className={styles.package}>
                    {salary.varMin} à {salary.varMax}k
                  </p>
                </div>
              </td>
            );
          }
        }
      });
    return partOfArray;
  };

  React.useEffect(() => {
    switch (apiResponse) {
      case "success":
        showSuccessState();
        setOpenShareModal(true);
        break;
      case "fail":
        showFailState();
        break;
    }
  }, [apiResponse]);

  const showSuccessState = () => {
    setSuccessLoader(true);
    setShowDownloadCta(true);
    anime({
      targets: checkCrossContainer.current,
      translateY: ["-100%", "0%"],
    });
  };

  const showFailState = () => {
    setFailLoader(true);
    anime({
      targets: checkCrossContainer.current,
      translateY: ["100%", "0%"],
    });
  };

  const triggerPdfDownload = (node: HTMLElement) => {
    const tm = setTimeout(() => {
      node.click();
      clearTimeout(tm);
    }, 2000);
  };

  const onFocusInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setFocusedInput(e.currentTarget.id);
  };
  const onBlurInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id;
    const indexDirty = dirtyIds.findIndex((eId: string) => eId === id);
    if (focusedInput === id) {
      setFocusedInput("");
    }
    if (indexDirty === -1) {
      setDirtyIds([...dirtyIds, id]);
    }

    checkInputValidity(e);
    triggerValidation(id);
  };
  const onChangeInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id;
    const val = e.currentTarget.value;
    const idsList = [...hasValueIds];
    const index = idsList.findIndex((eId: string) => eId === id);

    if (val) {
      if (index === -1) {
        setHasValueIds([...idsList, id]);
      }
    } else {
      if (index !== -1) {
        idsList.splice(index, 1);
        setHasValueIds(idsList);
      }
    }

    checkInputValidity(e);
  };
  const checkInputValidity = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id;
    const idsList = [...invalidIds];
    const index = idsList.findIndex((eId: string) => eId === id);

    if (!e.currentTarget.checkValidity()) {
      if (index === -1) {
        setInvalidIds([...idsList, id]);
      }
    } else {
      if (index !== -1) {
        idsList.splice(index, 1);
        setInvalidIds(idsList);
      }
    }
  };

  const createInput = (id: string, classes = [], type = "text") => {
    const isNotEmpty = hasValueIds.some(e => e === id);
    const isDirty = dirtyIds.some(e => e === id);
    const isNotValid = invalidIds.some(e => e === id) || !!errors[id];
    const isFocused = focusedInput === id;

    return (
      <>
        {errors && errors[id] && errors[id].message ? <span className={styles.error}>{errors[id].message}</span> : null}
        <input
          className={cx(classes, { isNotEmpty }, { isFocused }, { isDirty }, { isNotValid })}
          ref={register}
          type={type}
          name={id}
          id={id}
          onFocus={onFocusInput}
          onBlur={onBlurInput}
          onChange={onChangeInput}
          placeholder="Votre email"
        />
      </>
    );
  };

  const triggerConversion = () => {
    LinkedInTag.track(process.env.GATSBY_LINKEDIN_CONVERSION_EMAIL_ID);
    // ReactPixel.track("Lead", { content_name: "DL simulateur salaire BtoB" });
  };

  const triggerConversionOutbound = () => {
    LinkedInTag.track(process.env.GATSBY_LINKEDIN_CONVERSION_SITE_ID);
    // ReactPixel.track("Lead", {
    //   content_name: "site web simulateur salaire BtoB",
    // });
  };

  React.useEffect(() => {
    setSalaries(state.salaries);
  }, [state]);

  React.useEffect(() => {}, [state]);

  if (state.role) {
    return (
      <div className={styles.searchResultInnerContainer}>
        <div className={styles.topLine} />
        {!mobile ? (
          <div className={styles.tableLegendContainer}>
            <table>
              <tbody>
                <tr>
                  <td></td>
                  <td>0 à 2 ans</td>
                  <td>3 à 5 ans</td>
                  <td>6 à 10 ans</td>
                  <td>11 à 15 ans</td>
                  <td>&gt;15 ans</td>
                </tr>
                {state.paris ? (
                  <tr>
                    <td className={styles.cityBlock}>Paris</td>
                    {filterSalaries("Paris")}
                  </tr>
                ) : null}
                {state.province ? (
                  <tr>
                    <td className={styles.cityBlock}>Province</td>
                    {filterSalaries("Province")}
                  </tr>
                ) : null}
              </tbody>
            </table>
            <div className={styles.legend}>
              <span className={styles.salaries}>Fixe</span>
              <span className={styles.package}>Variable</span>
            </div>
          </div>
        ) : (
          <div className={styles.tableLegendContainer}>
            <table>
              <tbody>
                <tr>
                  <td>
                    <div className={styles.legend}>
                      <span className={styles.legendWording}>*Légende</span>
                      <div>
                        <span className={styles.salaries}>Fixe</span>
                        <span className={styles.package}>Variable</span>
                      </div>
                    </div>
                  </td>
                  {state.paris ? <td className={styles.cityBlock}>Paris</td> : null}
                  {state.province ? <td className={styles.cityBlock}>Province</td> : null}
                </tr>
                <tr>
                  <td>0 à 2 ans</td>
                  {state.paris ? filterSalariesForMobile("Paris", "0 - 2 ans") : null}
                  {state.province ? filterSalariesForMobile("Province", "0 - 2 ans") : null}
                </tr>
                <tr>
                  <td>3 à 5 ans</td>
                  {state.paris ? filterSalariesForMobile("Paris", "3 - 5 ans") : null}
                  {state.province ? filterSalariesForMobile("Province", "3 - 5 ans") : null}
                </tr>
                <tr>
                  <td>6 à 10ans</td>
                  {state.paris ? filterSalariesForMobile("Paris", "6 - 10 ans") : null}
                  {state.province ? filterSalariesForMobile("Province", "6 - 10 ans") : null}
                </tr>
                <tr>
                  <td>11 à 15 ans</td>
                  {state.paris ? filterSalariesForMobile("Paris", "11 - 15 ans") : null}
                  {state.province ? filterSalariesForMobile("Province", "11 - 15 ans") : null}
                </tr>
                <tr>
                  <td>+ 15 ans</td>
                  {state.paris ? filterSalariesForMobile("Paris", "15+ ans") : null}
                  {state.province ? filterSalariesForMobile("Province", "15+ ans") : null}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.informationsData}>
          <label className={styles.label} htmlFor="email">
            Télécharger les grilles des salaires par métier :
          </label>
          <div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmitForm)}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <div className={styles.formGroupContent}>{createInput("email", [], "email")}</div>
                </div>
              </div>
              <div className={styles.formSubmitContainer}>
                <div className={styles.submitCtaContainer} ref={btnSubmitContainer}>
                  <button
                    className={cx(styles.btn, styles.btnPrimary)}
                    disabled={disableBtn}
                    ref={btnDownload}
                    type="submit"
                  >
                    Télécharger
                  </button>
                  <span
                    className={cx(
                      styles.loader,
                      { animate: animateLoader },
                      { success: successLoader },
                      { fail: failLoader },
                    )}
                    ref={loader}
                  >
                    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <circle r="8" cx="10" cy="10" />
                    </svg>
                  </span>
                  <span className={styles.checkCrossContainer} ref={checkCrossContainer}>
                    <svg
                      className={cx(styles.check, {
                        [styles.show]: successLoader,
                      })}
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#check"></use>
                    </svg>
                    <svg
                      className={cx(styles.cross, {
                        [styles.show]: failLoader,
                      })}
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M13.7 10l5.5-5.5c.7-.7.7-1.8 0-2.4L18 .9c-.7-.7-1.8-.7-2.4 0L10 6.3 4.5.8C3.8.1 2.7.1 2.1.8L.8 2.1c-.7.7-.7 1.8 0 2.4L6.3 10 .8 15.5c-.7.7-.7 1.8 0 2.4L2 19.1c.7.7 1.8.7 2.4 0l5.5-5.5 5.5 5.5c.7.7 1.8.7 2.4 0l1.2-1.2c.7-.7.7-1.8 0-2.4L13.7 10z" />
                      </svg>
                    </svg>
                  </span>
                </div>
                {showDownloadCta ? (
                  <div className={styles.startDownload}>
                    si le téléchargement n'est pas automatique,{" "}
                    <a
                      href={linkToPdf}
                      ref={pdfCta}
                      target="_blank"
                      rel="noreferrer noopener"
                      download="simulateur-btob"
                    >
                      cliquez ici
                    </a>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
          <div className={styles.contactUs}>
            Besoin d’aide pour recruter le talent qui fera la différence ?{" "}
            <b className={styles.styledText}>
              <a
                href="mailto:contact@elinoi.com"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - contactez-nous",
                  });
                }}
              >
                Contactez-nous.
              </a>
            </b>
            <div className={styles.buttonCTA}>
              <a
                href="https://elinoi.com/startups"
                target="_blank"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - en savoir plus",
                  });
                  triggerConversionOutbound();
                }}
              >
                En savoir plus sur elinoï
              </a>
            </div>
          </div>
          <div className={styles.shareLink}>
            Et si vous pensez que l’un de vos collègues peut être intéressé par le sujet, <b>partagez-lui</b> :
            <div className={styles.logoBlock}>
              <a
                href={gmailLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - partage - email",
                  });
                }}
              >
                <Img className={styles.iconMail} fluid={data.logoEMail.childImageSharp.fluid} alt="mail logo" />
              </a>
              <a
                href={facebookLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - partage - facebook",
                  });
                }}
              >
                <Img className={styles.icon} fluid={data.logoFacebook.childImageSharp.fluid} alt="facebook logo" />
              </a>
              <a
                href={linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - partage - linkedin",
                  });
                }}
              >
                <Img className={styles.icon} fluid={data.logoLinkedin.childImageSharp.fluid} alt="linkedin logo" />
              </a>
            </div>
          </div>
          <div className={styles.details}>
            <h3>Vous vous demandez d'où viennent toutes ses informations ?</h3>
            <p>
              Ces données sont la synthèse d’un scrapping puis d'une analyse rigoureuse :
              <span>
                <br />
                <span>
                  1/ du marché sur plus de 30 000 offres d'emplois publiées par
                  des centaines de startups et jeunes PMEs françaises
                </span>
                <br />
                <span>
                  2/ de notre expérience en tant que partenaire de recrutement depuis 4 ans et des salaires d’entrée des
                  centaines de talents recrutés via l’incubateur.
                </span>
              </span>
            </p>
            <br />
            <p>
              Si certains chiffres ou associations peuvent vous étonner, sachez que nous avons ensuite développé un
              algorithme qui, au vu des données récoltées sur le marché, pondère et nous donne la simulation de salaire
              pour ce cas particulier même s’il est peu commun. Ces informations sont indicatives. Elle n’ont pas valeur
              de vérité absolue et ont pour unique but de vous donner une vision précise et réaliste du marché de
              l’emploi aujourd’hui grâce à des points de comparaison. Si vous avez des feedbacks à nous faire, n'hésitez
              pas à contacter{" "}
              <a
                href="mailto:contact@elinoi.com"
                onClick={() => {
                  gtagService.event("click", {
                    event_label: "cta - bottom text - contact email",
                  });
                }}
              >
                contact@elinoi.com.
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default SearchResult;
